import { useCallback, useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Avatar,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useAuthStore } from '@/store/auth'
import {
  canAssignSupportAgent,
  getAssignAgentListPath,
  isTeamLead,
} from '@/lib/utils/roleHelper'
import { UserService, type UserAvailabilityStatus } from '@/lib/services/userService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  useAvailabilityCatalog,
  setAvailabilityCache,
  availabilityStatusColor,
  availabilityStatusLabel,
  isUserAvailabilityStatus,
} from '@/lib/userAvailability'
import { useEffect } from 'react'

function formatStatusLabel(value: unknown): string {
  if (value == null || value === '') return '–'
  return availabilityStatusLabel(String(value))
}

export default function AgentStatusPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  const access = useAccessCheck()
  const user = useAuthStore((s) => s.user)
  const canManageAgentStatus = canAssignSupportAgent(user)
  const userIsTeamLead = isTeamLead(user)
  const agentsDataSource = useMemo(() => getAssignAgentListPath(user), [user])
  const { showSuccess, showError } = useToastNotification()
  const { agentStatusOptions, definitions: availabilityDefinitions } = useAvailabilityCatalog()

  useEffect(() => {
    if (availabilityDefinitions.length > 0) {
      setAvailabilityCache(availabilityDefinitions)
    }
  }, [availabilityDefinitions])

  const userStatusFilterOptions = useMemo(() => agentStatusOptions, [agentStatusOptions])
  const STATUS_OPTIONS = agentStatusOptions

  const handleStatusChange = useCallback(
    async (userId: number, status: UserAvailabilityStatus) => {
      setUpdatingId(userId)
      try {
        await UserService.updateMemberStatus(userId, status)
        showSuccess('Success', `Status updated to ${formatStatusLabel(status)}`)
        tableRef.current?.reload()
      } catch (error) {
        showError(error as Error)
      } finally {
        setUpdatingId(null)
      }
    },
    [showSuccess, showError]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: false,
      dataFields: {
        first_name: {
          name: 'Name',
          withSort: true,
          maxWidth: '220px',
        },
        availability_status: {
          name: 'User Status',
          withSort: false,
          withFilter: true,
          filterOptions: userStatusFilterOptions,
          maxWidth: canManageAgentStatus ? '150px' : '120px',
        },
        user_email: {
          name: 'Email',
          withSort: false,
          maxWidth: '240px',
        },
        role_name: {
          name: 'Role',
          withSort: true,
          maxWidth: '140px',
        },
        assigned_tickets_count: {
          name: 'Assigned Tickets',
          withSort: true,
          maxWidth: '120px',
        },
        active_tickets_count: {
          name: 'Active Tickets',
          withSort: false,
          maxWidth: '120px',
        },
      },
      dataSource: agentsDataSource,
      primaryKey: 'id',
      softDelete: false,
      displayInModal: false,
      noRecordText: userIsTeamLead
        ? 'No team members found. Users reporting to you will appear here.'
        : 'No assignable agents found.',
      hideSelection: true,
      otherActions: [],
    }),
    [userStatusFilterOptions, canManageAgentStatus, agentsDataSource, userIsTeamLead]
  )

  const permissions = useMemo(
    () => ({
      can_edit: false,
      can_delete: false,
      can_create: false,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access.can_view]
  )

  const tableParams = useMemo(
    () => ({
      per_page: 10,
      ...(searchQuery ? { search: searchQuery } : {}),
    }),
    [searchQuery]
  )

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Agent Status
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              {userIsTeamLead
                ? 'Team members reporting to you with assigned and active (open status) ticket counts'
                : 'Assignable support agents with assigned and active (open status) ticket counts'}
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0} px={4}>
              <Box borderBottom="2px" borderColor={colors.border} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by name, email..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody
            p={0}
            pt={3}
            pb={4}
            mx={5}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            position="relative"
          >
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                customRenderers={{
                  first_name: (value, row) => {
                    const first = row?.first_name ?? ''
                    const last = row?.last_name ?? ''
                    const name = [first, last].filter(Boolean).join(' ') || row?.user_login || '–'
                    const availabilityStatus = row?.availability_status ?? 'offline'
                    const statusColor = availabilityStatusColor(availabilityStatus)
                    return (
                      <HStack spacing={2} align="center" minW={0}>
                        <Avatar
                          size="sm"
                          name={name}
                          bg={statusColor}
                          color="white"
                          flexShrink={0}
                          w="24px"
                          h="24px"
                        />
                        <Tooltip label={name} hasArrow placement="top">
                          <Text as="span" noOfLines={1} display="block" w="100%" minW={0}>
                            {name}
                          </Text>
                        </Tooltip>
                      </HStack>
                    )
                  },
                  user_email: (value) => {
                    const str = value ? String(value) : '–'
                    return (
                      <Tooltip label={str} hasArrow placement="top">
                        <Text as="span" noOfLines={1} display="block" w="100%" minW={0}>
                          {str}
                        </Text>
                      </Tooltip>
                    )
                  },
                  role_name: (value) => (value ? String(value) : '–'),
                  availability_status: (value, row) => {
                    const raw = value ? String(value) : 'offline'
                    const current: UserAvailabilityStatus = isUserAvailabilityStatus(raw)
                      ? raw
                      : 'offline'
                    if (!canManageAgentStatus) {
                      return <Text as="span">{formatStatusLabel(current)}</Text>
                    }

                    const rowId = Number(row?.id)
                    const isUpdating = updatingId === rowId
                    const inDropdown = STATUS_OPTIONS.some((o) => o.value === current)
                    // Legacy break/lunch: keep selectable until supervisor changes them
                    const selectOptions = inDropdown
                      ? STATUS_OPTIONS
                      : [
                          { value: current, label: formatStatusLabel(current) },
                          ...STATUS_OPTIONS,
                        ]

                    return (
                      <Select
                        size="sm"
                        value={current}
                        isDisabled={isUpdating || !rowId}
                        onChange={(e) => {
                          const next = e.target.value as UserAvailabilityStatus
                          if (next === current || !rowId) return
                          void handleStatusChange(rowId, next)
                        }}
                        maxW="180px"
                      >
                        {selectOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    )
                  },
                  assigned_tickets_count: (value) => {
                    const n = typeof value === 'number' ? value : parseInt(String(value ?? 0), 10)
                    return (
                      <Text as="span" fontWeight="600">
                        {Number.isNaN(n) ? '0' : n}
                      </Text>
                    )
                  },
                  active_tickets_count: (value) => {
                    const n = typeof value === 'number' ? value : parseInt(String(value ?? 0), 10)
                    return (
                      <Text as="span" fontWeight="600">
                        {Number.isNaN(n) ? '0' : n}
                      </Text>
                    )
                  },
                }}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  )
}
